import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileQuestionIcon } from 'lucide-react';
import { Button, EmptyState, ErrorNotice, PageHeading, Panel, Skeleton } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  NEW_TEMPLATE,
  OUTREACH_QUERY_KEYS,
  OUTREACH_TEMPLATES_PATH,
  OUTREACH_TEMPLATE_SCOPE,
  copyTeamTemplate,
  listMyTemplates,
  listPlaceholders,
  listTeamTemplates,
  outreachTemplatePath,
  placeholderToken,
  type OutreachPlaceholder,
  type OutreachTemplate,
  type OutreachTemplateScope,
} from '@/lib/outreach-templates';
import { TemplateForm } from '@/components/outreach-templates/TemplateForm';
import { TemplatePreview } from '@/components/outreach-templates/TemplatePreview';

/** How long "Copied" shows after copying a template's text. */
const COPIED_FOR_MS = 2_000;

export interface OutreachTemplatePageProps {
  /** Which kind of template the route is for. */
  readonly scope: OutreachTemplateScope;
}

const COPY: Readonly<Record<OutreachTemplateScope, { readonly newTitle: string; readonly subtitle: string }>> = {
  [OUTREACH_TEMPLATE_SCOPE.personal]: {
    newTitle: 'Write a template',
    subtitle: 'Only you see your templates. Drafts for your clients start from them.',
  },
  [OUTREACH_TEMPLATE_SCOPE.team]: {
    newTitle: 'Add a team template',
    subtitle: 'Every rep sees team templates and can use or copy them.',
  },
};

/**
 * One outreach template on its own page (`/sales/email-templates/:id`, or
 * `/team/:id` for the team's), or a blank one at `.../new`. A rep's own
 * template, or a team template for a manager, opens as the form; a rep
 * opening a team template reads it and can copy its text or copy it to
 * their own. The templates are read from the lists, which are small.
 *
 * @param props - Whether the route is for the rep's own templates or the team's.
 * @returns The page.
 */
export function OutreachTemplatePage({ scope }: OutreachTemplatePageProps): React.ReactElement {
  const { templateId = NEW_TEMPLATE } = useParams();
  const navigate = useNavigate();
  const isNew = templateId === NEW_TEMPLATE;
  const isTeam = scope === OUTREACH_TEMPLATE_SCOPE.team;

  const mine = useQuery({
    queryKey: OUTREACH_QUERY_KEYS.mine,
    queryFn: ({ signal }) => listMyTemplates(signal),
    enabled: !isTeam && !isNew,
  });
  const team = useQuery({ queryKey: OUTREACH_QUERY_KEYS.team, queryFn: ({ signal }) => listTeamTemplates(signal), enabled: isTeam });
  const placeholders = useQuery({
    queryKey: OUTREACH_QUERY_KEYS.placeholders,
    queryFn: ({ signal }) => listPlaceholders(signal),
    staleTime: Infinity,
  });

  const backToList = () => navigate(OUTREACH_TEMPLATES_PATH);
  const list = isTeam ? team : mine;
  const templates: readonly OutreachTemplate[] = isTeam ? (team.data?.templates ?? []) : (mine.data ?? []);
  const template = isNew ? null : (templates.find((candidate) => candidate.id === templateId) ?? null);
  const canEdit = !isTeam || team.data?.canManage === true;
  const loading = (!isNew && list.isPending) || (isTeam && team.isPending) || placeholders.isPending;

  const heading = (title: string, subtitle?: string) => (
    <PageHeading
      backTo={OUTREACH_TEMPLATES_PATH}
      breadcrumbs={[{ label: 'Sales' }, { label: 'My email templates', to: OUTREACH_TEMPLATES_PATH }]}
      title={title}
      {...(subtitle ? { subtitle } : {})}
    />
  );

  if (list.isError || placeholders.isError) {
    return (
      <section>
        {heading('Template')}
        <ErrorNotice title="Couldn’t load the template" message={errorMessage(list.error ?? placeholders.error)} />
      </section>
    );
  }
  if (loading) {
    return (
      <section>
        {heading(isNew ? COPY[scope].newTitle : 'Template')}
        <Skeleton className="h-96 w-full" />
      </section>
    );
  }
  if (!isNew && template === null) {
    return (
      <section>
        {heading('Template')}
        <Panel>
          <EmptyState
            icon={<FileQuestionIcon aria-hidden className="size-6" />}
            title="Template not found"
            message="It may have been removed, or it belongs to someone else."
            action={<Button onClick={backToList}>Back to templates</Button>}
          />
        </Panel>
      </section>
    );
  }
  if (isNew && !canEdit) {
    return (
      <section>
        {heading(COPY[scope].newTitle)}
        <Panel>
          <EmptyState
            title="Managers write team templates"
            message="Write one of your own instead; only you see it."
            action={<Button to={outreachTemplatePath(OUTREACH_TEMPLATE_SCOPE.personal, NEW_TEMPLATE)}>Write a template</Button>}
          />
        </Panel>
      </section>
    );
  }

  if (template !== null && !canEdit) {
    return (
      <section className="animate-portal-rise">
        {heading(template.name, 'A team template. The assistant can draft from it as it is, or copy it to make it yours.')}
        <UseTeamTemplate template={template} placeholders={placeholders.data ?? []} />
      </section>
    );
  }

  return (
    <section className="animate-portal-rise">
      {heading(template?.name ?? COPY[scope].newTitle, COPY[scope].subtitle)}
      {/* Keyed so moving between templates starts the form from the one shown. */}
      <TemplateForm
        key={template?.id ?? NEW_TEMPLATE}
        scope={scope}
        template={template}
        placeholders={placeholders.data ?? []}
        onCreated={(created) => navigate(outreachTemplatePath(scope, created.id), { replace: true })}
        onDone={backToList}
      />
    </section>
  );
}

/** A team template for a rep: read it, copy its text, or copy it into their own. */
function UseTeamTemplate({
  template,
  placeholders,
}: {
  readonly template: OutreachTemplate;
  readonly placeholders: readonly OutreachPlaceholder[];
}): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const copyToMine = useMutation({
    mutationFn: () => copyTeamTemplate(template.id),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: OUTREACH_QUERY_KEYS.mine });
      navigate(outreachTemplatePath(OUTREACH_TEMPLATE_SCOPE.personal, created.id));
    },
  });
  const copyText = async () => {
    await navigator.clipboard.writeText(`${template.subject}\n\n${template.body}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-5">
        <TemplatePreview subject={template.subject} body={template.body} placeholders={placeholders} />
        <p className="text-portal-micro text-portal-muted">
          Copying the text keeps the placeholders, like {placeholderToken('first_name')}, for you to fill in.
        </p>
        {copyToMine.isError ? <ErrorNotice inline title="Template not copied" message={errorMessage(copyToMine.error)} /> : null}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {copied ? <span className="mr-auto text-portal-micro font-bold text-emerald-600">Copied</span> : null}
          <Button variant="secondary" onClick={() => void copyText()}>
            Copy text
          </Button>
          <Button disabled={copyToMine.isPending} onClick={() => copyToMine.mutate()}>
            {copyToMine.isPending ? 'Copying…' : 'Copy to mine'}
          </Button>
        </div>
      </div>
      {template.whenToUse ? (
        <Panel className="self-start p-5">
          <p className="text-portal-micro font-bold text-portal-muted uppercase">When to use it</p>
          <p className="mt-1 text-portal-small">{template.whenToUse}</p>
        </Panel>
      ) : null}
    </div>
  );
}
